import axios from "axios";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import ServiceCatalogAPI from "../api/service-catalog";
import { createLogger } from "../lib/logger";
import {
  AppStore,
  ColorTheme,
  CreateServiceData,
  Deployment,
  LoginCredentials,
  Project,
  RegisterCredentials,
  Service,
  ServiceEnvironmentConfig,
  Theme,
  User,
} from "../types";
import { CreateServiceFromCatalogData } from "../types/service-catalog";
// Central logger (replaces scattered console.*) — see docs/logging.md.

const log = createLogger("store");

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8003/api";

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Auth state
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      // UI state
      sidebarCollapsed: false,
      mobileNavOpen: false,
      theme: "system",
      colorTheme: "flame",

      // Projects state
      projects: [],
      selectedProject: null,

      // Services state
      services: [],
      selectedService: null,

      // Deployments state
      deployments: [],

      // Service Catalog state
      serviceCatalog: {},
      serviceCatalogLoading: false,

      // Auth actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true });
          const response = await axios.post(
            `${API_BASE}/auth/login`,
            credentials
          );
          const { token, user } = response.data;

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });

          // Set axios default header
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          localStorage.setItem("avahana-token", token);
        } catch (error: any) {
          set({ isLoading: false });
          throw new Error(error.response?.data?.error || "Login failed");
        }
      },

      register: async (credentials: RegisterCredentials) => {
        try {
          set({ isLoading: true });
          const response = await axios.post(
            `${API_BASE}/auth/register`,
            credentials
          );
          const { token, user } = response.data;

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });

          // Set axios default header
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          localStorage.setItem("avahana-token", token);
        } catch (error: any) {
          set({ isLoading: false });
          throw new Error(error.response?.data?.error || "Registration failed");
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          projects: [],
          selectedProject: null,
          services: [],
          selectedService: null,
          deployments: [],
        });
        localStorage.removeItem("avahana-token");
        delete axios.defaults.headers.common["Authorization"];

        // Reload page to trigger App re-render with logged-out state
        window.location.replace("/auth/login");
      },

      setUser: (user: User | null) => set({ user, isAuthenticated: !!user }),
      setToken: (token: string | null) => set({ token }),
      setLoading: (isLoading: boolean) => set({ isLoading }),

      // API functions
      fetchProjects: async () => {
        try {
          const response = await axios.get(`${API_BASE}/projects`);
          set({ projects: response.data.projects });
          return response.data.projects;
        } catch (error) {
          log.error("Failed to fetch projects:", error);
          throw error;
        }
      },

      fetchServices: async (projectId: string, environmentId?: string) => {
        try {
          let url = `${API_BASE}/projects/${projectId}/services`;
          if (environmentId) {
            url += `?environmentId=${environmentId}`;
          }
          const response = await axios.get(url);
          set({ services: response.data.services });
          return response.data.services;
        } catch (error) {
          log.error("Failed to fetch services:", error);
          throw error;
        }
      },

      fetchDeployments: async (
        projectId?: string,
        serviceId?: string,
        environmentId?: string
      ) => {
        try {
          let url;
          if (projectId) {
            url = `${API_BASE}/projects/${projectId}/deployments`;
          } else {
            url = `${API_BASE}/deployments`;
          }

          const params = new URLSearchParams();
          if (serviceId) params.append("serviceId", serviceId);
          if (environmentId) params.append("environmentId", environmentId);
          if (params.toString()) url += `?${params.toString()}`;

          const response = await axios.get(url);
          set({ deployments: response.data.deployments || response.data });
          return response.data.deployments || response.data;
        } catch (error) {
          log.error("Failed to fetch deployments:", error);
          throw error;
        }
      },

      fetchUserProfile: async () => {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`);
          set({ user: response.data.user });
          return response.data.user;
        } catch (error) {
          log.error("Failed to fetch user profile:", error);
          throw error;
        }
      },

      updateUserProfile: async (updates: { name?: string }) => {
        try {
          const response = await axios.put(`${API_BASE}/auth/me`, updates);
          set({ user: response.data.user });
          return response.data.user;
        } catch (error) {
          log.error("Failed to update user profile:", error);
          throw error;
        }
      },

      // API functions for services with environment management
      createService: async (
        projectId: string,
        serviceData: CreateServiceData
      ) => {
        try {
          const response = await axios.post(
            `${API_BASE}/projects/${projectId}/services`,
            serviceData
          );
          const newService = response.data.service;
          const deployment = response.data.deployment; // May be undefined

          set((state) => ({
            services: [...state.services, newService],
            // Add deployment to list if it was created
            deployments: deployment
              ? [...state.deployments, deployment]
              : state.deployments,
          }));

          return { service: newService, deployment };
        } catch (error) {
          log.error("Failed to create service:", error);
          throw error;
        }
      },

      updateService: async (serviceId: string, updates: Partial<Service>) => {
        try {
          const response = await axios.put(
            `${API_BASE}/services/${serviceId}`,
            updates
          );
          const updatedService = response.data.service;
          set((state) => ({
            services: state.services.map((s) =>
              s.id === serviceId ? updatedService : s
            ),
            selectedService:
              state.selectedService?.id === serviceId
                ? updatedService
                : state.selectedService,
          }));
          return updatedService;
        } catch (error) {
          log.error("Failed to update service:", error);
          throw error;
        }
      },

      deleteService: async (serviceId: string) => {
        try {
          await axios.delete(`${API_BASE}/services/${serviceId}`);
          set((state) => ({
            services: state.services.filter((s) => s.id !== serviceId),
            selectedService:
              state.selectedService?.id === serviceId
                ? null
                : state.selectedService,
          }));
        } catch (error) {
          log.error("Failed to delete service:", error);
          throw error;
        }
      },

      // Environment management for services
      updateServiceEnvironment: async (
        serviceId: string,
        environment: string,
        config: ServiceEnvironmentConfig
      ) => {
        try {
          const response = await axios.put(
            `${API_BASE}/services/${serviceId}/environments/${environment}`,
            config
          );
          const updatedService = response.data.service;
          set((state) => ({
            services: state.services.map((s) =>
              s.id === serviceId ? updatedService : s
            ),
            selectedService:
              state.selectedService?.id === serviceId
                ? updatedService
                : state.selectedService,
          }));
          return updatedService;
        } catch (error) {
          log.error("Failed to update service environment:", error);
          throw error;
        }
      },

      // Project environment management
      updateProjectEnvironment: async (
        projectId: string,
        environment: string,
        envVars: Record<string, string>
      ) => {
        try {
          const response = await axios.put(
            `${API_BASE}/projects/${projectId}/environments/${environment}`,
            { envVars }
          );
          const updatedProject = response.data.project;
          set((state) => ({
            projects: state.projects.map((p) =>
              p.id === projectId ? updatedProject : p
            ),
            selectedProject:
              state.selectedProject?.id === projectId
                ? updatedProject
                : state.selectedProject,
          }));
          return updatedProject;
        } catch (error) {
          log.error("Failed to update project environment:", error);
          throw error;
        }
      },

      // Deploy services with environment coordination
      deployServices: async (
        projectId: string,
        serviceIds: string[],
        environment: string
      ) => {
        try {
          const response = await axios.post(
            `${API_BASE}/projects/${projectId}/deploy`,
            {
              serviceIds,
              environment,
            }
          );
          const deployments = response.data.deployments;
          set((state) => ({
            deployments: [...state.deployments, ...deployments],
          }));
          return deployments;
        } catch (error) {
          log.error("Failed to deploy services:", error);
          throw error;
        }
      },

      // Promote environment (staging -> production)
      promoteEnvironment: async (
        projectId: string,
        fromEnvironment: string,
        toEnvironment: string
      ) => {
        try {
          const response = await axios.post(
            `${API_BASE}/projects/${projectId}/promote`,
            {
              fromEnvironment,
              toEnvironment,
            }
          );
          const result = response.data;

          // Refresh project and services data after promotion
          await get().fetchProjects();
          await get().fetchServices(projectId);

          return result;
        } catch (error) {
          log.error("Failed to promote environment:", error);
          throw error;
        }
      },

      // UI actions
      // NOTE: the desktop rail is permanently collapsed (see Sidebar.tsx), so
      // nothing reads `sidebarCollapsed` any more. Kept so the persisted key
      // stays valid and a future expandable rail has somewhere to hook in —
      // but setting it today has no visible effect.
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed: boolean) =>
        set({ sidebarCollapsed }),
      setMobileNavOpen: (mobileNavOpen: boolean) => set({ mobileNavOpen }),
      // Theme actions
      setTheme: (theme: Theme) => {
        set({ theme });
        const root = window.document.documentElement;
        root.classList.remove("light", "dark");

        if (theme === "system") {
          const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
            .matches
            ? "dark"
            : "light";
          root.classList.add(systemTheme);
        } else {
          root.classList.add(theme);
        }
      },

      setColorTheme: (colorTheme: ColorTheme) => {
        set({ colorTheme });
        document.documentElement.setAttribute("data-theme", colorTheme);
      },

      // Projects actions
      setProjects: (projects: Project[]) => set({ projects }),
      setSelectedProject: (selectedProject: Project | null) =>
        set({ selectedProject }),
      addProject: (project: Project) =>
        set((state) => ({
          projects: [...state.projects, project],
        })),
      updateProject: (projectId: string, updates: Partial<Project>) =>
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, ...updates } : p
          ),
          selectedProject:
            state.selectedProject?.id === projectId
              ? { ...state.selectedProject, ...updates }
              : state.selectedProject,
        })),
      removeProject: (projectId: string) =>
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== projectId),
          selectedProject:
            state.selectedProject?.id === projectId
              ? null
              : state.selectedProject,
        })),

      // Services actions
      setServices: (services: Service[]) => set({ services }),
      setSelectedService: (selectedService: Service | null) =>
        set({ selectedService }),
      addService: (service: Service) =>
        set((state) => ({
          services: [...state.services, service],
        })),
      removeService: (serviceId: string) =>
        set((state) => ({
          services: state.services.filter((s) => s.id !== serviceId),
          selectedService:
            state.selectedService?.id === serviceId
              ? null
              : state.selectedService,
        })),

      // Deployments actions
      setDeployments: (deployments: Deployment[]) => set({ deployments }),
      addDeployment: (deployment: Deployment) =>
        set((state) => ({
          deployments: [...state.deployments, deployment],
        })),
      updateDeployment: (deploymentId: string, updates: Partial<Deployment>) =>
        set((state) => ({
          deployments: state.deployments.map((d) =>
            d.id === deploymentId ? { ...d, ...updates } : d
          ),
        })),

      // Service Catalog actions
      setServiceCatalog: (serviceCatalog: Record<string, any[]>) =>
        set({ serviceCatalog }),
      setServiceCatalogLoading: (serviceCatalogLoading: boolean) =>
        set({ serviceCatalogLoading }),

      // Service Catalog API functions
      fetchServiceCatalog: async () => {
        // Check if we already have service catalog data loaded
        const currentState = get();
        if (
          Object.keys(currentState.serviceCatalog).length > 0 &&
          !currentState.serviceCatalogLoading
        ) {
          log.debug("Service catalog already loaded, skipping fetch");
          return currentState.serviceCatalog;
        }

        try {
          set({ serviceCatalogLoading: true });
          const catalog = await ServiceCatalogAPI.getAllTemplates();
          set({ serviceCatalog: catalog, serviceCatalogLoading: false });
          return catalog;
        } catch (error) {
          log.error("Failed to fetch service catalog:", error);
          set({ serviceCatalogLoading: false });
          throw error;
        }
      },

      searchServiceTemplates: async (query: string) => {
        try {
          return await ServiceCatalogAPI.searchTemplates(query);
        } catch (error) {
          log.error("Failed to search service templates:", error);
          throw error;
        }
      },

      createServiceFromTemplate: async (
        projectId: string,
        data: CreateServiceFromCatalogData
      ) => {
        try {
          return await ServiceCatalogAPI.createServiceFromTemplate(
            projectId,
            data
          );
        } catch (error) {
          log.error("Failed to create service from template:", error);
          throw error;
        }
      },

      initializeServiceCatalog: async () => {
        try {
          await ServiceCatalogAPI.initializeCatalog();
        } catch (error) {
          log.error("Failed to initialize service catalog:", error);
          throw error;
        }
      },
    }),
    {
      name: "avahana-store",
      // v1: `colorTheme` was defaulted to "sky" while the [data-theme] blocks
      // did not exist, so the preset was inert and every user saw the flame
      // brand. Now that presets work, that stale default would silently
      // re-skin the app blue — migrate it back to the brand.
      version: 1,
      migrate: (persisted: unknown, fromVersion: number) => {
        const state = persisted as { colorTheme?: ColorTheme } | undefined;
        if (state && fromVersion < 1 && state.colorTheme === "sky") {
          return { ...state, colorTheme: "flame" as ColorTheme };
        }
        return state;
      },
      partialize: (state) => ({
        theme: state.theme,
        colorTheme: state.colorTheme,
        sidebarCollapsed: state.sidebarCollapsed,
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        // Don't persist projects and services data as it should be fresh on reload
      }),
      onRehydrateStorage: () => (state) => {
        // Restore axios authorization header when store is rehydrated
        if (state?.token) {
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${state.token}`;
        }
      },
    }
  )
);

// Initialize store on app start
export const initializeStore = async () => {
  const token = localStorage.getItem("avahana-token");
  log.debug("Initializing store with token:", token ? "FOUND" : "NOT FOUND");

  if (token) {
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const response = await axios.get(`${API_BASE}/auth/me`);
      log.debug("Token validation successful, setting authenticated state");

      // Set all authentication state at once
      useAppStore.setState({
        user: response.data.user,
        token: token,
        isAuthenticated: true,
        isLoading: false,
      });

      // Load service catalog data for authenticated users
      try {
        log.debug("Loading service catalog for authenticated user");
        await useAppStore.getState().fetchServiceCatalog();
        log.debug("Service catalog loaded successfully");
      } catch (error) {
        log.error("Failed to load service catalog:", error);
        // Don't fail initialization if service catalog fails to load
      }
    } catch (error) {
      // Token is invalid, remove it
      log.debug("Token validation failed, logging out user", error);
      localStorage.removeItem("avahana-token");
      delete axios.defaults.headers.common["Authorization"];
      useAppStore.setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  } else {
    log.debug("No token found, setting unauthenticated state");
    useAppStore.setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }
};

// Make store available for debugging in development
if (process.env.NODE_ENV === "development") {
  (window as any).useAppStore = useAppStore;
  (window as any).initializeStore = initializeStore;
}
